import { uuidv4 } from '@firebase/util';
import { Inject, Injectable } from '@angular/core';
import { getDatabase, ref, get, set, update, remove, query, orderByChild, startAfter, limitToFirst } from 'firebase/database';

import { Firestore } from '@angular/fire/firestore';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export abstract class FirebaseRTDBService<T> {
  protected abstract basePath: string;
  debug: boolean = environment.debug;


  constructor(@Inject(Firestore) protected fireStore: Firestore) {
  }



  /**
   * Retrieves a collection from Realtime Database.
   *
   * @param queryFn Optional. A function that returns a query. Use this to filter the data.
   * @param orderByName Optional. The name of the field by which the collection should be ordered.
   * @param pageSize The maximum number of documents to return per page.
   * @param startAfterValue Optional. A value to start after for pagination.
   */
  async getCollection$(
    queryFn?: (queryRef: any) => any,
    orderByName?: string | null,
    pageSize: number = 100,
    startAfterValue?: any
  ): Promise<any[]> {
    const db = getDatabase();
    const collectionRef = ref(db, `${this.basePath}`);

    let q = collectionRef;

    if (queryFn) {
      // Apply custom query
      q = queryFn(collectionRef);
    } else if (orderByName) {
      if (startAfterValue) {
        q = query(collectionRef, orderByChild(orderByName), startAfter(startAfterValue), limitToFirst(pageSize)) as any;
      } else {
        q = query(collectionRef, orderByChild(orderByName), limitToFirst(pageSize)) as any;
      }
    }

    try {
      const snapshot = await get(q);
      if (snapshot.exists()) {
        return Object.entries(snapshot.val()).map(([key, value]: [string, any]) => ({
          uid: key,
          ...value,
        }))
      }
      return [];
    } catch (error) {
      console.error('Error fetching data from Realtime Database:', error);
      throw error;
    }
  }

  /**
   * Retrieves a document by ID from Realtime Database.
   *
   * @param id The ID of the document to retrieve.
   */
  async getDocument(id: string): Promise<T | false> {
    const db = getDatabase();
    const docRef = ref(db, `${this.basePath}/${id}`);

    try {
      const snapshot = await get(docRef);
      if (snapshot.exists()) {
        return snapshot.val() as T;
      }
      return false;
    } catch (error) {
      console.error('Error fetching document from Realtime Database:', error);
      throw error;
    }
  }

  /**
   * Creates a new document in Realtime Database.
   *
   * @param value The data to be stored in the new document.
   * @param uid Optional. The unique identifier for the document. If not provided, a new UUID will be generated.
   */
  async create(value: any, uid?: string): Promise<T | any> {
    if (!uid) {
      uid = uuidv4();
      value.uid = uid;
    }

    const db = getDatabase();
    const docRef = ref(db, `${this.basePath}/${uid}`);

    try {
      await set(docRef, value);
      this.update(value, uid)
      return 'success';
    } catch (error) {
      return error
    }
  }

  /**
   * Updates an existing document in Realtime Database.
   *
   * @param value The updated data.
   * @param uid The unique identifier of the document.
   */
  async update(value: any, uid: string): Promise<T | any> {
    const db = getDatabase();
    const docRef = ref(db, `${this.basePath}/${uid}`);

    try {
      await update(docRef, value);
      return 'success';
    } catch (error) {
      return error
    }
  }

  /**
   * Deletes a document from Realtime Database.
   *
   * @param id The ID of the document to delete.
   */
  async delete(id: string): Promise<string> {
    const db = getDatabase();
    const docRef = ref(db, `${this.basePath}/${id}`);

    try {
      await remove(docRef);
      return 'Success'
    } catch (error: any) {
      return error;
    }
  }

  /**
   * Removes empty or null values from an object.
   *
   * @param obj The object to clean.
   */
  removeEmpty(obj: any): any {
    const newObj: any = {};
    Object.keys(obj).forEach((key) => {
      if (obj[key] === Object(obj[key])) {
        newObj[key] = this.removeEmpty(obj[key]);
      } else if (obj[key] !== null && obj[key] !== undefined) {
        newObj[key] = obj[key];
      }
    });
    return newObj;
  }

}
